import { AbstractAtlasControl } from 'app/src/features/Showcase/helpers/AbstractAtlasControl'
import type { IAbstractAtlasControlOptions } from 'app/src/features/Showcase/helpers/maputils'
import * as atlas from 'azure-maps-control'

/** Options for the BringDataIntoViewControl. */
interface BringDataIntoViewControlOptions extends IAbstractAtlasControlOptions {
  /**
   * The style of the control. Can be; light, dark, auto, or any CSS3 color. When set to auto, the style will change based on the map style.
   * Default `light'.
   * @default light
   */
  style?: atlas.ControlStyle | string

  /** The amount of pixel padding around the data to account for when setting the map view. */
  padding?: number

  /** Default Zoom */
  defaultZoom?: number

  units?: string

  i18n?: {
    restorePosition: string
  }
}

/** A control that makes it easy to bring any data loaded on the map into view. */
export class BringDataIntoViewControl extends AbstractAtlasControl<BringDataIntoViewControlOptions> {
  private static defaultOptions: BringDataIntoViewControlOptions = {
    style: 'light',
    padding: 100,
  }

  constructor(options?: Partial<BringDataIntoViewControlOptions>) {
    super(options ? { ...options, ...BringDataIntoViewControl.defaultOptions } : { ...BringDataIntoViewControl.defaultOptions })
  }
  protected getAriaLabel(): string {
    return this._options?.i18n?.restorePosition ?? ''
  }
  /**
   * Action to perform when the control is added to the map.
   * @param map The map the control was added to.
   * @param options The control options used when adding the control to the map.
   * @returns The HTML Element that represents the control.
   */
  public onAdd(map: atlas.Map): HTMLElement {
    this._map = map
    const ariaLabel = this.getAriaLabel()

    let color = this._options?.style || 'light'

    if (color === 'light') {
      color = 'white'
    } else if (color === 'dark') {
      color = this._darkColor
    } else if (color === 'auto') {
      //Color will change between light and dark depending on map style.
      this._map.events.add('styledata', () => {
        this.mapStyleChanged()
      })
      color = this.getColorFromMapStyle()
    }

    //Create a button container.
    this._container = document.createElement('div')
    this._container.classList.add('azure-maps-control-container')
    this._container.setAttribute('aria-label', ariaLabel)
    this._container.style.flexDirection = 'column'

    //Create the button.
    this._button = document.createElement('button')
    this._button.classList.add('atlas-map-customControl')
    this._button.classList.add('atlas-map-bringDataIntoViewBtn')
    this._button.style.backgroundColor = color
    this._button.setAttribute('title', ariaLabel)
    this._button.setAttribute('alt', ariaLabel)
    this._button.setAttribute('type', 'button')
    this._button.addEventListener('click', () => {
      this._options?.onControlClickCallBack?.()
    })

    this._container.appendChild(this._button)
    return this._container
  }

  /**
   * Action to perform when control is removed from the map.
   */
  public onRemove(): void {
    if (this._container) {
      this._container.remove()
      this._container = undefined
    }

    if (this._options?.style === 'auto') {
      this._map?.events.remove('styledata', () => {
        this.mapStyleChanged()
      })
    }
    this._map = undefined
  }
}
